import React, { useState, useEffect, useCallback } from 'react';
import {
  Smartphone,
  Users,
  UserCheck,
  Repeat,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Mail,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { adocaoAppAPI } from '../services/api';
import StatCard from '../components/StatCard';

// ---------------------------------------------------------------------------
// Constantes de apresentacao
// ---------------------------------------------------------------------------
const COR = {
  acessou: '#20a490',
  parou: '#f39c12',
  nunca: '#e74c3c',
  sem_cadastro: '#95a5a6',
  cadastro: '#3498db',
};

// A situacao olha o ULTIMO acesso, nao o mes de referencia: quem entrou no mes
// corrente continua ativo, ainda que nao apareca nas contagens do mes fechado.
const SITUACAO = {
  acessou: { rot: 'Ativo', cor: COR.acessou, desc: 'acessou no mês de referência ou depois' },
  parou_de_acessar: { rot: 'Parou de acessar', cor: COR.parou, desc: 'último acesso é anterior ao mês de referência' },
  nunca_acessou: { rot: 'Nunca acessou', cor: COR.nunca, desc: 'tem cadastro e nunca entrou' },
  sem_cadastro: { rot: 'Sem cadastro', cor: COR.sem_cadastro, desc: 'não tem cadastro no app' },
};

const MES_EXT = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
                 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const nomeMes = (ym) => (ym ? MES_EXT[Number(ym.slice(5, 7)) - 1] : '');
const rotuloMes = (ym) => (ym ? `${ym.slice(5, 7)}/${ym.slice(2, 4)}` : '');
const num = (v, d = 0) =>
  (v === null || v === undefined)
    ? '—'
    : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });

const dataBR = (iso) => {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a.slice(2)}`;
};

// ---------------------------------------------------------------------------
// Grafico de barras + linha, mes a mes (SVG, sem dependencia externa)
// ---------------------------------------------------------------------------
const GraficoMensal = ({ serie }) => {
  const [hover, setHover] = useState(null);
  if (!serie?.length) return null;

  const W = 900, H = 300;
  const pad = { l: 44, r: 16, t: 18, b: 38 };
  const pw = W - pad.l - pad.r;
  const ph = H - pad.t - pad.b;

  const max = Math.max(...serie.map((s) => s.ja_tinham_cadastro), 10);
  const teto = Math.ceil(max / 20) * 20;
  const y = (v) => pad.t + ph - (v / teto) * ph;
  const banda = pw / serie.length;
  const cx = (i) => pad.l + banda * (i + 0.5);
  const bw = Math.min(28, banda * 0.4);

  const linha = (campo) =>
    serie.map((s, i) => `${i ? 'L' : 'M'}${cx(i)} ${y(s[campo])}`).join('');

  const ticks = [0, teto / 4, teto / 2, (teto * 3) / 4, teto];

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}
           role="img" aria-label="Cadastros e acessos por mês">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={pad.l + pw} y1={y(t)} y2={y(t)} stroke="#eceff1" strokeWidth="1" />
            <text x={pad.l - 8} y={y(t) + 4} textAnchor="end"
                  style={{ fontSize: 11, fill: '#95a5a6' }}>{num(t)}</text>
          </g>
        ))}

        {/* area: quantas pessoas ja tinham cadastro */}
        <path d={`${linha('ja_tinham_cadastro')}L${cx(serie.length - 1)} ${y(0)}L${cx(0)} ${y(0)}Z`}
              fill="rgba(52,152,219,0.12)" />
        <path d={linha('ja_tinham_cadastro')} fill="none" stroke={COR.cadastro} strokeWidth="2" />

        {/* barras: novos cadastros no mes */}
        {serie.map((s, i) => {
          const h = ph - (y(s.novos_cadastros) - pad.t);
          return (
            <rect key={s.mes} x={cx(i) - bw / 2} y={y(s.novos_cadastros)}
                  width={bw} height={Math.max(h, 0)} rx="3" fill={COR.parou}
                  opacity={hover === null || hover === i ? 1 : 0.45} />
          );
        })}

        {/* linha: quantas pessoas acessaram */}
        <path d={linha('pessoas_que_acessaram')} fill="none" stroke={COR.acessou} strokeWidth="2" />
        {serie.map((s, i) => (
          <circle key={s.mes} cx={cx(i)} cy={y(s.pessoas_que_acessaram)} r="4"
                  fill={COR.acessou} stroke="#fff" strokeWidth="2" />
        ))}

        <line x1={pad.l} x2={pad.l + pw} y1={y(0)} y2={y(0)} stroke="#cfd8dc" strokeWidth="1" />
        {serie.map((s, i) => (
          <text key={s.mes} x={cx(i)} y={y(0) + 18} textAnchor="middle"
                style={{ fontSize: 11, fill: '#7f8c8d' }}>{rotuloMes(s.mes)}</text>
        ))}

        {/* faixas de hover */}
        {serie.map((s, i) => (
          <rect key={s.mes} x={pad.l + banda * i} y={pad.t} width={banda} height={ph}
                fill="transparent" style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
        ))}
      </svg>

      {hover !== null && (
        <div style={{
          position: 'absolute', top: 8,
          left: `${((pad.l + banda * (hover + 0.5)) / W) * 100}%`,
          transform: hover > serie.length / 2 ? 'translate(-105%,0)' : 'translate(8px,0)',
          background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6,
          padding: '9px 12px', fontSize: '0.78rem', boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
          pointerEvents: 'none', minWidth: 190, zIndex: 5,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {nomeMes(serie[hover].mes)} de {serie[hover].mes.slice(0, 4)}
          </div>
          <Row cor={COR.cadastro} rot="Já tinham cadastro" val={num(serie[hover].ja_tinham_cadastro)} />
          <Row cor={COR.acessou} rot="Acessaram" val={num(serie[hover].pessoas_que_acessaram)} />
          <Row cor={COR.parou} rot="Novos cadastros" val={num(serie[hover].novos_cadastros)} />
          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #f0f0f0', color: '#7f8c8d' }}>
            {num(serie[hover].pct_que_acessou, 1)}% acessou · {num(serie[hover].acessos)} acessos
            {serie[hover].acessos_sem_autor > 0 &&
              ` (+${num(serie[hover].acessos_sem_autor)} sem autor)`}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ cor, rot, val }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
    <span style={{ width: 9, height: 9, borderRadius: 2, background: cor, flex: 'none' }} />
    <span>{rot}</span>
    <strong style={{ marginLeft: 'auto' }}>{val}</strong>
  </div>
);

const Legenda = ({ itens }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', margin: '14px 0 4px',
                fontSize: '0.8rem', color: '#546e7a' }}>
    {itens.map((i) => (
      <span key={i.rot} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <span style={{ width: 11, height: 11, borderRadius: 2, background: i.cor, flex: 'none' }} />
        {i.rot}
      </span>
    ))}
  </div>
);

const Plate = ({ titulo, sub, children, acao }) => (
  <div style={{ background: '#fff', border: '1px solid #eceff1', borderRadius: 8,
                padding: '20px 22px', marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
      <div style={{ flex: 1 }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#2c3e50' }}>{titulo}</h3>
        {sub && <p style={{ margin: '6px 0 0', fontSize: '0.83rem', color: '#607d8b',
                            lineHeight: 1.5, maxWidth: '74ch' }}>{sub}</p>}
      </div>
      {acao}
    </div>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------
const DashboardAdocaoApp = () => {
  const [carregando, setCarregando] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [coortes, setCoortes] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [aberta, setAberta] = useState(null);
  const [filtro, setFiltro] = useState('todos');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [r, e, c, p] = await Promise.all([
        adocaoAppAPI.resumo(),
        adocaoAppAPI.empresas(),
        adocaoAppAPI.coortes(),
        adocaoAppAPI.pessoas(),
      ]);
      setResumo(r.data);
      setEmpresas(e.data.empresas || []);
      setCoortes(c.data.coortes || []);
      setPessoas(p.data.pessoas || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível carregar a adoção do app.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  if (carregando) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#7f8c8d' }}>
        <RefreshCw size={22} className="spin" style={{ marginBottom: 10 }} />
        <div>Carregando adoção do app…</div>
      </div>
    );
  }

  if (!resumo) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#e74c3c' }}>
        <AlertCircle size={22} style={{ marginBottom: 10 }} />
        <div>Não foi possível ler os dados do app.</div>
      </div>
    );
  }

  // O mes corrente fica fora do grafico: comparar mes em andamento com mes
  // fechado distorce a leitura.
  const serieFechada = resumo.serie.filter((s) => s.mes <= resumo.mes_referencia);
  const ref = resumo.serie.find((s) => s.mes === resumo.mes_referencia) || {};
  const primeiro = resumo.serie[0] || {};
  const mesRef = nomeMes(resumo.mes_referencia);

  const semCadastro = resumo.situacoes.sem_cadastro || 0;
  const comCadastro = resumo.beneficiarios - semCadastro;

  const semAcesso = pessoas.filter(
    (p) => p.situacao === 'nunca_acessou' || p.situacao === 'sem_cadastro'
  );
  const comEmailErrado = semAcesso.filter((p) => p.email_de_terceiro);
  const acionaveis = semAcesso.filter((p) => !p.email_de_terceiro);

  const pessoasFiltradas = (lista) =>
    filtro === 'todos' ? lista : lista.filter((p) => p.situacao === filtro);

  const acessosPorPessoaRef = ref.acessos_por_pessoa;
  const acessosPorPessoaIni = primeiro.acessos_por_pessoa;

  return (
    <div style={{ padding: '24px 28px 60px', maxWidth: 1180, margin: '0 auto' }}>
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10,
                     color: '#2c3e50' }}>
          <Smartphone size={22} color={COR.acessou} /> Adoção do App
        </h2>
        <p style={{ margin: '8px 0 0', color: '#607d8b', fontSize: '0.9rem', maxWidth: '70ch' }}>
          Quantos beneficiários têm o app, quantos realmente usam e quem nunca conseguiu
          entrar. Mês de referência: <strong>{mesRef} de {resumo.mes_referencia.slice(0, 4)}</strong>{' '}
          — o último mês fechado.
        </p>
      </div>

      {/* ── KPIs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))',
                    gap: 14, marginBottom: 22 }}>
        <StatCard title="Beneficiários com cadastro" value={comCadastro}
                  subtitle={semCadastro === 0
                    ? `todos os ${num(resumo.beneficiarios)} já criaram o acesso`
                    : `${num(semCadastro)} ainda sem cadastro`}
                  icon={<Users size={22} />} color={COR.cadastro} />
        <StatCard title={`Acessaram em ${mesRef}`} value={ref.pessoas_que_acessaram}
                  subtitle={`${num(ref.pct_que_acessou, 1)}% de quem já tinha cadastro (${num(ref.ja_tinham_cadastro)})`}
                  icon={<UserCheck size={22} />} color={COR.acessou} />
        <StatCard title={`Novos cadastros em ${mesRef}`} value={ref.novos_cadastros}
                  subtitle="entraram no app pela primeira vez"
                  icon={<Smartphone size={22} />} color={COR.parou} />
        <StatCard title="Acessos por pessoa" value={num(acessosPorPessoaRef, 1)}
                  subtitle={`${num(ref.acessos)} acessos de ${num(ref.pessoas_que_acessaram)} pessoas · em ${nomeMes(primeiro.mes)} era ${num(acessosPorPessoaIni, 1)}`}
                  icon={<Repeat size={22} />} color="#9b59b6" />
      </div>

      {/* ── Quem nao conseguiu entrar ── */}
      {semAcesso.length > 0 && (
        <Plate
          titulo="Quem não conseguiu entrar"
          sub={`${semAcesso.length} pessoas estão na base de beneficiários e nunca acessaram o app. ${acionaveis.length} é gente para procurar; ${comEmailErrado.length} tem e-mail de outra pessoa no cadastro e precisa de correção antes de qualquer contato.`}
        >
          <TabelaPessoas lista={semAcesso} mostrarEmpresa destaque mesRef={mesRef} />
        </Plate>
      )}

      {/* ── Curva mensal ── */}
      <Plate
        titulo="Cadastros e acessos, mês a mês"
        sub="Tudo aqui é contagem de pessoas. A área mostra quantas já tinham cadastro; a linha, quantas de fato entraram no app naquele mês; as barras, quantas se cadastraram pela primeira vez."
      >
        <Legenda itens={[
          { rot: 'Já tinham cadastro', cor: COR.cadastro },
          { rot: 'Acessaram no mês', cor: COR.acessou },
          { rot: 'Novos cadastros', cor: COR.parou },
        ]} />
        <GraficoMensal serie={serieFechada} />
      </Plate>

      {/* ── Situacao da base ── */}
      <Plate
        titulo="Situação de cada beneficiário"
        sub={`Todos os ${num(resumo.beneficiarios)} beneficiários, divididos pelo que fizeram no app em ${mesRef}.`}
      >
        <div style={{ display: 'flex', gap: 2, height: 34, marginTop: 8 }}>
          {['acessou', 'parou_de_acessar', 'nunca_acessou', 'sem_cadastro']
            .filter((st) => resumo.situacoes[st])
            .map((st) => {
              const n = resumo.situacoes[st];
              return (
                <div key={st} title={`${SITUACAO[st].rot}: ${n} de ${resumo.beneficiarios}`}
                     style={{ flex: n, background: SITUACAO[st].cor, borderRadius: 3,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontSize: '0.78rem', fontWeight: 600 }}>
                  {n >= 8 ? n : ''}
                </div>
              );
            })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', marginTop: 12,
                      fontSize: '0.8rem', color: '#546e7a' }}>
          {['acessou', 'parou_de_acessar', 'nunca_acessou', 'sem_cadastro']
            .filter((st) => resumo.situacoes[st])
            .map((st) => (
              <span key={st} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 11, height: 11, borderRadius: 2,
                               background: SITUACAO[st].cor, flex: 'none' }} />
                {SITUACAO[st].rot} · <strong>{resumo.situacoes[st]}</strong>{' '}
                ({num(100 * resumo.situacoes[st] / resumo.beneficiarios, 0)}%)
              </span>
            ))}
        </div>
      </Plate>

      {/* ── Por empresa ── */}
      <Plate
        titulo="Por empresa"
        sub="O comprimento total de cada barra é o tamanho da empresa. A parte escura é quem acessou no mês de referência; a parte clara, quem já acessou em algum momento. Clique na empresa para ver a lista de pessoas."
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '14px 0 6px' }}>
          {[['todos', 'Todos'], ['acessou', 'Ativos'],
            ['parou_de_acessar', 'Pararam'], ['nunca_acessou', 'Nunca acessaram']]
            .map(([v, rot]) => (
              <button key={v} onClick={() => setFiltro(v)}
                      style={{
                        fontSize: '0.75rem', padding: '6px 11px', borderRadius: 4,
                        cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em',
                        border: `1px solid ${filtro === v ? '#2c3e50' : '#e0e0e0'}`,
                        background: filtro === v ? '#2c3e50' : '#fff',
                        color: filtro === v ? '#fff' : '#546e7a',
                      }}>{rot}</button>
            ))}
        </div>

        {empresas.map((g) => {
          const maiorBase = empresas[0].beneficiarios;
          const pAcessou = (g.acessaram_no_mes / g.beneficiarios) * 100;
          const pJa = (g.ja_acessaram / g.beneficiarios) * 100;
          const abertaAqui = aberta === g.empresa;
          const daEmpresa = pessoasFiltradas(pessoas.filter((p) => p.empresa === g.empresa));

          return (
            <div key={g.empresa} style={{ borderBottom: '1px solid #f4f6f6' }}>
              <button
                onClick={() => setAberta(abertaAqui ? null : g.empresa)}
                aria-expanded={abertaAqui}
                style={{ width: '100%', border: 0, background: 'transparent', cursor: 'pointer',
                         padding: '12px 8px', textAlign: 'left', display: 'block' }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12,
                              fontSize: '0.87rem', marginBottom: 6 }}>
                  <span style={{ fontWeight: 500, color: '#2c3e50' }}>
                    {g.empresa}
                    <small style={{ display: 'block', fontWeight: 400, color: '#90a4ae',
                                    fontSize: '0.76rem', marginTop: 2 }}>
                      {g.beneficiarios} beneficiários · {g.titulares} titulares,{' '}
                      {g.dependentes} dependentes
                      {g.nunca_acessaram > 0 && (
                        <span style={{ color: COR.nunca }}>
                          {' '}· {g.nunca_acessaram} nunca acessaram
                        </span>
                      )}
                    </small>
                  </span>
                  <span style={{ marginLeft: 'auto', color: '#546e7a', whiteSpace: 'nowrap',
                                 display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ color: '#2c3e50' }}>{g.acessaram_no_mes}</strong>{' '}
                    acessaram · {num(g.pct_que_acessou, 1)}%
                    <ChevronRight size={15}
                      style={{ transform: abertaAqui ? 'rotate(90deg)' : 'none',
                               transition: 'transform 0.15s' }} />
                  </span>
                </div>
                <div style={{ position: 'relative', height: 11, borderRadius: 3,
                              background: '#eceff1', overflow: 'hidden',
                              width: `${(g.beneficiarios / maiorBase) * 100}%` }}>
                  <div style={{ position: 'absolute', inset: '0 auto 0 0', borderRadius: 3,
                                width: `${pJa}%`, background: 'rgba(32,164,144,0.32)' }} />
                  <div style={{ position: 'absolute', inset: '0 auto 0 0', borderRadius: 3,
                                width: `${pAcessou}%`, background: COR.acessou }} />
                </div>
              </button>

              {abertaAqui && (
                <div style={{ padding: '0 8px 16px' }}>
                  <TabelaPessoas lista={daEmpresa} mesRef={mesRef} />
                </div>
              )}
            </div>
          );
        })}
      </Plate>

      {/* ── Coortes ── */}
      <Plate
        titulo="Quem se cadastrou em cada mês, e quantos voltaram"
        sub={`Cada linha é o grupo que se cadastrou naquele mês. Quase todos entram no app no mesmo dia em que se cadastram, porque o cadastro acontece justamente na primeira entrada. A última coluna mostra quantos desse grupo ainda estavam entrando em ${mesRef}.`}
      >
        <div style={{ overflowX: 'auto', marginTop: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem',
                          minWidth: 620 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                <Th>Mês do cadastro</Th>
                <Th right>Cadastraram</Th>
                <Th right>Chegaram a acessar</Th>
                <Th right>%</Th>
                <Th right>Acessou no 1º dia</Th>
                <Th right>Acessaram em {mesRef}</Th>
                <Th right>%</Th>
              </tr>
            </thead>
            <tbody>
              {coortes.map((c) => {
                const antesDoLancamento = c.mes_cadastro < '2026-03';
                return (
                  <tr key={c.mes_cadastro}
                      style={{ borderBottom: '1px solid #f4f6f6',
                               color: antesDoLancamento ? '#b0bec5' : '#37474f' }}>
                    <Td>
                      {rotuloMes(c.mes_cadastro)}
                      {antesDoLancamento && (
                        <span style={{ marginLeft: 7, fontSize: '0.68rem', padding: '2px 6px',
                                       border: '1px solid #e0e0e0', borderRadius: 3,
                                       color: '#90a4ae' }}>antes do lançamento</span>
                      )}
                    </Td>
                    <Td right>{num(c.cadastraram)}</Td>
                    <Td right>{num(c.chegaram_a_acessar)}</Td>
                    <Td right>{num(c.pct_chegaram_a_acessar, 1)}%</Td>
                    <Td right>{num(c.acessou_no_mesmo_dia)}</Td>
                    <Td right>{num(c.acessaram_no_mes_ref)}</Td>
                    <Td right>
                      <span style={{
                        display: 'inline-block', minWidth: 52, padding: '3px 0',
                        borderRadius: 3, textAlign: 'center', fontWeight: 600,
                        background: c.pct_acessaram_no_mes_ref >= 60 ? '#20a490'
                          : c.pct_acessaram_no_mes_ref >= 30 ? '#a5dbd1' : '#f4f6f6',
                        color: c.pct_acessaram_no_mes_ref >= 60 ? '#fff' : '#37474f',
                      }}>{num(c.pct_acessaram_no_mes_ref, 1)}%</span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Plate>

      {/* ── De onde vem os numeros ── */}
      <Plate titulo="De onde vêm estes números">
        <dl style={{ margin: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Nota titulo="O que conta como acesso">
            Cada vez que alguém faz login no app, o sistema gera uma credencial que vale um
            dia. Contamos essas credenciais: uma equivale a uma entrada no app. Não é
            possível usar o registro de “último acesso” do sistema, porque ele nunca foi
            preenchido — está vazio para todas as {num(resumo.meta.contas_no_sistema)} contas.
          </Nota>
          <Nota titulo="Quem ficou fora da conta">
            {num(resumo.meta.cadastros_de_teste)} cadastros que não são de beneficiários
            reais (demonstração e testes) e {num(resumo.meta.contas_internas)} contas
            internas de administrador. Sobraram {num(resumo.beneficiarios)} beneficiários.
          </Nota>
          <Nota titulo="O começo da série é incompleto">
            O registro de acessos só existe a partir de 20/02/2026. Quem se cadastrou antes
            disso pode ter entrado no app sem que apareça aqui, por isso a série começa em
            março, quando o app foi aberto aos beneficiários.
          </Nota>
          {ref.acessos_sem_autor > 0 && (
            <Nota titulo="Acessos que não dá para atribuir">
              {num(ref.acessos_sem_autor)} entradas de {mesRef} não têm registro de quem as
              fez, por uma falha antiga de gravação. Isso não esconde nenhuma pessoa das
              listas, só deixa a contagem de acessos um pouco abaixo do real.
            </Nota>
          )}
          <Nota titulo="Mês de referência">
            Os indicadores usam o último mês <strong>fechado</strong> ({mesRef}). O mês
            corrente fica fora dos comparativos porque ainda está em andamento.
          </Nota>
        </dl>
      </Plate>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pecinhas
// ---------------------------------------------------------------------------
const Th = ({ children, right }) => (
  <th style={{ padding: '9px 10px', textAlign: right ? 'right' : 'left',
               fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em',
               color: '#90a4ae', fontWeight: 600, whiteSpace: 'nowrap' }}>{children}</th>
);

const Td = ({ children, right }) => (
  <td style={{ padding: '9px 10px', textAlign: right ? 'right' : 'left',
               fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{children}</td>
);

const Nota = ({ titulo, children }) => (
  <div>
    <dt style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em',
                 color: '#90a4ae', fontWeight: 600, marginBottom: 4 }}>{titulo}</dt>
    <dd style={{ margin: 0, fontSize: '0.83rem', lineHeight: 1.6, color: '#546e7a',
                 maxWidth: '78ch' }}>{children}</dd>
  </div>
);

const TabelaPessoas = ({ lista, mostrarEmpresa, destaque, mesRef }) => {
  if (!lista.length) {
    return <p style={{ fontSize: '0.83rem', color: '#90a4ae', margin: '12px 0 0' }}>
      Ninguém neste filtro.
    </p>;
  }
  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem',
                      minWidth: 700 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
            <Th>Nome</Th>
            <Th>Matrícula</Th>
            {mostrarEmpresa && <Th>Empresa</Th>}
            <Th>Tipo</Th>
            <Th>Situação</Th>
            <Th right>Dias em {mesRef}</Th>
            <Th right>Acessos em {mesRef}</Th>
            <Th>Último acesso</Th>
            <Th>E-mail</Th>
          </tr>
        </thead>
        <tbody>
          {lista.map((p, i) => (
            <tr key={`${p.matricula}-${i}`}
                style={{ borderBottom: '1px solid #f4f6f6',
                         background: destaque && p.email_de_terceiro ? '#fdf3f2' : undefined }}>
              <Td>{p.nome || '—'}</Td>
              <Td>{p.matricula || '—'}</Td>
              {mostrarEmpresa && <Td>{p.empresa}</Td>}
              <Td>{p.tipo === 'TITULAR' ? 'Titular' : 'Dependente'}</Td>
              <Td>
                <span style={{ fontSize: '0.7rem', padding: '2px 7px', borderRadius: 3,
                               color: '#fff', whiteSpace: 'nowrap',
                               background: SITUACAO[p.situacao]?.cor || '#95a5a6' }}>
                  {SITUACAO[p.situacao]?.rot || p.situacao}
                </span>
              </Td>
              <Td right>{p.dias_no_mes || '—'}</Td>
              <Td right>{p.acessos_mes || '—'}</Td>
              <Td>{dataBR(p.ultimo_acesso)}</Td>
              <Td>
                {p.email || <span style={{ color: COR.nunca }}>sem e-mail</span>}
                {p.email_de_terceiro && (
                  <span title="O e-mail cadastrado pertence a outra pessoa"
                        style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center',
                                 gap: 3, fontSize: '0.68rem', padding: '2px 6px',
                                 border: `1px solid ${COR.nunca}`, borderRadius: 3,
                                 color: COR.nunca, whiteSpace: 'nowrap' }}>
                    <Mail size={10} /> de outra pessoa
                  </span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DashboardAdocaoApp;
